import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { setup_product_requirements } from "services/apiSrv";
import { show_toast } from "utils/helpers";
import { useParams } from "react-router-dom";

// Validation schema
const validationSchema = Yup.object().shape({
  requirementId: Yup.array()
    .min(1, "At least one requirement must be selected")
    .required("Requirement is required"),
});

export default function AddKycRequirement({ refetchProductDetails, requirementsItems }) {
  const { id: productSystemRef } = useParams();

  const kycOptions = [
    { id: "email", name: "Email Address", description: "Valid email address of the client", isRequired: true, isUnique: true, validation: "MANUAL" },
    { id: "krapin", name: "KRA PIN", description: "Kenya Revenue Authority Personal Identification Number", isRequired: true, isUnique: true, validation: "MANUAL" },
    { id: "phoneno", name: "Phone Number", description: "Client's mobile phone number", isRequired: true, isUnique: true, validation: "MANUAL" },
    { id: "nationalid", name: "National ID", description: "National Identification Number", isRequired: true, isUnique: true, validation: "MANUAL" },
    { id: "passno", name: "Passport Number", description: "Client's passport number", isRequired: true, isUnique: true, validation: "MANUAL" },
  ];

  // Initialize requirementId with IDs from requirementsItems
  const initialValues = {
    requirementId: requirementsItems
      ? requirementsItems
          .filter((item) => item.type === "KYC") // Only include KYC requirements
          .map((item) => item.formCode)
          .filter((id) => kycOptions.some((option) => option.id === id))
      : [],
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Build the array of selected requirements
      const productRequirements = values.requirementId.map((reqId) => {
        const selected = kycOptions.find((item) => item.id === reqId);
        return {
          name: selected.name,
          description: selected.description,
          isRequired: selected.isRequired,
          isUnique: selected.isUnique,
          validationType: selected.validation,
          formCode: selected.id,
          type: "KYC",
        };
      });

      // Send single request with array
      await setup_product_requirements({
        productSystemRef,
        productRequirements,
      });

      show_toast("KYC Requirement(s) added successfully!", "success");
      refetchProductDetails?.();
      resetForm();
    } catch (error) {
      show_toast("Error adding KYC requirement: " + error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 bg-white rounded shadow-sm my-4">
      <h4 className="mb-4">Add KYC Requirement</h4>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize // Ensures form reinitializes when requirementsItems changes
      >
        {({ isSubmitting }) => (
          <Form className="mb-3">
            <div className="mb-3">
              <label className="form-label fw-semibold">
                Select KYC Requirements
              </label>
              <div className="d-flex flex-column gap-2">
                {kycOptions.map((item) => (
                  <label key={item.id} className="form-check">
                    <Field
                      type="checkbox"
                      name="requirementId"
                      value={item.id}
                      className="form-check-input"
                    />
                    <span className="form-check-label">{item.name}</span>
                  </label>
                ))}
              </div>
              <ErrorMessage
                name="requirementId"
                component="div"
                className="invalid-feedback d-block"
              />
            </div>

            <button
              type="submit"
              className="btn btn-success px-4 py-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Adding..." : "Add KYC Requirement(s)"}
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
}